import { Box, Card, Stack, Typography, Skeleton, Button } from '@mui/material';

import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';
import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';
import { PageHeader } from '@/pwa/atoms/PageHeader/PageHeader';
import { PortableTextViewer } from '@/components/organisms/RichTextEditor/PortableTextViewer';
import type { PortableTextContent } from '@/components/organisms/RichTextEditor/portableText';

export interface CallNoteViewPageProps {
  title: string;
  content?: PortableTextContent;
  createdAt?: string;
  authorName?: string;
  isLoading?: boolean;
  onBack?: () => void;
  onEdit?: () => void;
}

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

export const CallNoteViewPage = ({
  title,
  content,
  createdAt,
  authorName,
  isLoading = false,
  onBack,
  onEdit,
}: CallNoteViewPageProps) => {
  const description = [
    createdAt && formatDate(createdAt),
    authorName && `with ${authorName}`,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <ModalPageTemplate onBack={onBack}>
      <PageTemplate
        maxWidth='md'
        headerSlot={
          isLoading ? (
            <Box>
              <Skeleton variant='text' width='60%' height={40} />
              <Skeleton variant='text' width='40%' height={24} />
            </Box>
          ) : (
            <Stack
              direction='row'
              sx={{
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}
            >
              <PageHeader
                variant='h2'
                title={title}
                description={description || undefined}
              />
              {onEdit && (
                <Button variant='outlined' size='small' onClick={onEdit}>
                  Edit
                </Button>
              )}
            </Stack>
          )
        }
        contentSlot={
          <Stack spacing={2}>
            <Box>
              <Typography
                variant='overline'
                sx={{
                  color: 'text.secondary',
                  mb: 1,
                }}
              >
                CALL SUMMARY
              </Typography>
            </Box>
            {isLoading ? (
              <Card>
                <Box
                  sx={{
                    p: 2,
                  }}
                >
                  <Stack spacing={1}>
                    <Skeleton variant='text' width='90%' />
                    <Skeleton variant='text' width='75%' />
                    <Skeleton variant='text' width='85%' />
                    <Skeleton variant='text' width='60%' />
                  </Stack>
                </Box>
              </Card>
            ) : content && content.length > 0 ? (
              <Card>
                <Box
                  sx={{
                    p: 2,
                  }}
                >
                  <PortableTextViewer content={content} />
                </Box>
              </Card>
            ) : (
              <Box
                sx={{
                  bgcolor: 'custom.cherry.100',
                  borderRadius: 3,
                  p: 2,
                }}
              >
                <Typography variant='body1'>
                  Your call summary will appear here. You will be notified when
                  it is ready.
                </Typography>
              </Box>
            )}
          </Stack>
        }
      />
    </ModalPageTemplate>
  );
};

CallNoteViewPage.displayName = 'CallNoteViewPage';
export default CallNoteViewPage;
